/**
 * The world: meditation, the training spots' ambience (a bed that loops while the player is near,
 * and ticks scattered over it: water, drips, gusts, bamboo knocking, waves, bubbles, wind chimes),
 * lanterns, the temple gong, warp circles, doors and stairs, the guqin of the training-spot banner,
 * the boss, carriages and the jail. Also the cutscenes' own sounds (data/scenes.ts `sfx` cues).
 */
import type { TrainingSpotKind } from '@nine/shared';
import type { SfxKit } from '../kit';
import { registerSfx } from '../registry';
import { bowl, chime, drum, gong, note, ROOT_D, strum, whoosh, woodblock } from '../voices';

// ───────── meditation ─────────

registerSfx(
  'world.meditate.start',
  { label: 'เริ่มนั่งสมาธิ (ขันทิเบต)', cat: 'world', vary: 0, gap: 1, preview: 3.5 },
  (k) => bowl(k, { f: 293.66, dur: 3.2, vol: 0.1 }),
);

registerSfx(
  'world.meditate.hum',
  { label: 'ฮัมสมาธิ (วน)', cat: 'world', loop: true, vary: 0, preview: 4 },
  (k) => {
    // A low drone of root, fifth and octave beating slowly, and a breath rising and falling.
    k.tone({ f: 73.4, dur: Infinity, vol: 0.07, attack: 1.2 });
    k.tone({ f: 110.2, dur: Infinity, vol: 0.04, attack: 1.5 });
    k.tone({ f: 146.4, dur: Infinity, vol: 0.025, attack: 1.8 });
    k.noise({ color: 'pink', dur: Infinity, vol: 0.05, attack: 1.5, f: 700, q: 1.4, trem: [0.2, 0.9] });
  },
);

// ───────── training spots ─────────

/** The bed each kind of training spot loops (sfx/frame.ts), and the tick that falls over it. */
const BEDS: Record<TrainingSpotKind, { label: string; bed: (k: SfxKit) => void; tick: string }> = {
  waterfall: {
    label: 'น้ำตก',
    tick: 'amb.splash',
    bed: (k) => {
      k.noise({ color: 'pink', dur: Infinity, vol: 0.3, attack: 1, type: 'lowpass', f: 1600, q: 0.5 });
      k.noise({ dur: Infinity, vol: 0.05, attack: 1, f: 3500, q: 0.8, trem: [0.9, 0.3] });
      k.noise({ color: 'brown', dur: Infinity, vol: 0.2, attack: 1, type: 'lowpass', f: 220 });
    },
  },
  cave: {
    label: 'ถ้ำ',
    tick: 'amb.drip',
    bed: (k) => {
      k.noise({
        color: 'brown',
        dur: Infinity,
        vol: 0.12,
        attack: 1.5,
        type: 'lowpass',
        f: 160,
        trem: [0.1, 0.5],
      });
      k.tone({ f: 55, dur: Infinity, vol: 0.02, attack: 2 });
    },
  },
  peak: {
    label: 'ยอดผา',
    tick: 'amb.gust',
    bed: (k) => {
      k.noise({ color: 'pink', dur: Infinity, vol: 0.18, attack: 1.5, f: 500, q: 1.2, trem: [0.15, 0.7] });
      k.noise({ dur: Infinity, vol: 0.025, attack: 2, f: 1900, q: 5, trem: [0.23, 0.9] });
    },
  },
  bamboo: {
    label: 'ป่าไผ่',
    tick: 'amb.knock',
    bed: (k) => {
      k.noise({ dur: Infinity, vol: 0.06, attack: 1.5, f: 3200, q: 0.7, trem: [0.3, 0.8] });
      k.noise({
        color: 'crackle',
        dur: Infinity,
        vol: 0.12,
        attack: 1.5,
        type: 'highpass',
        f: 3000,
        trem: [0.4, 0.9],
      });
    },
  },
  hotSpring: {
    label: 'น้ำพุร้อน',
    tick: 'amb.bubble',
    bed: (k) => {
      k.noise({ color: 'pink', dur: Infinity, vol: 0.1, attack: 1.5, type: 'lowpass', f: 900 });
      k.noise({ dur: Infinity, vol: 0.02, attack: 1.5, type: 'highpass', f: 5000 });
    },
  },
  shrine: {
    label: 'ศาลร้าง',
    tick: 'amb.chime',
    bed: (k) => {
      k.noise({ color: 'pink', dur: Infinity, vol: 0.06, attack: 2, f: 400, q: 1, trem: [0.12, 0.6] });
      k.tone({ f: 146.8, dur: Infinity, vol: 0.012, attack: 3, vib: [0.2, 0.004] });
    },
  },
  shore: {
    label: 'ริมทะเล',
    tick: 'amb.wave',
    bed: (k) => {
      k.noise({
        color: 'pink',
        dur: Infinity,
        vol: 0.12,
        attack: 1.5,
        type: 'lowpass',
        f: 700,
        trem: [0.11, 0.7],
      });
      k.noise({ dur: Infinity, vol: 0.02, attack: 2, type: 'highpass', f: 4000, trem: [0.11, 0.8] });
    },
  },
};

/** The tick sound that is scattered over each spot's bed. */
export const SPOT_TICK: Readonly<Record<TrainingSpotKind, string>> = Object.fromEntries(
  Object.entries(BEDS).map(([kind, b]) => [kind, b.tick]),
) as Record<TrainingSpotKind, string>;

for (const [kind, b] of Object.entries(BEDS))
  registerSfx(
    `amb.${kind}`,
    { label: `บรรยากาศ${b.label} (วน)`, cat: 'ambient', loop: true, vary: 0, preview: 4 },
    (k) => b.bed(k),
  );

registerSfx('amb.splash', { label: 'น้ำกระเซ็น', cat: 'ambient', vary: 0.15, gap: 0.2 }, (k) => {
  k.noise({ dur: 0.3, vol: 0.12, attack: 0.02, f: 1800, f1: 900, q: 1 });
  k.tone({ f: k.range(600, 900), f1: k.range(1200, 1800), at: 0.05, dur: 0.06, vol: 0.02 });
});

registerSfx('amb.drip', { label: 'หยดน้ำในถ้ำ', cat: 'ambient', vary: 0.2, gap: 0.2, preview: 1.4 }, (k) => {
  // A drop into a pool: a rising plink and its echo off the rock.
  const cave = k.echo(0.19, 0.45, 0.4);
  k.tone({ f: 900, f1: 2000, sweep: 0.05, dur: 0.12, vol: 0.09, out: cave });
  k.click({ f: 3000, vol: 0.05, out: cave });
});

registerSfx(
  'amb.gust',
  { label: 'ลมกระโชกบนยอดเขา', cat: 'ambient', vary: 0.1, gap: 0.5, preview: 2.4 },
  (k) => {
    k.noise({ color: 'pink', dur: 2, vol: 0.2, attack: 0.8, f: 400, f1: 1100, f2: 500, sweep: 0.9, q: 1.5 });
    k.noise({ dur: 1.8, vol: 0.03, attack: 0.7, f: 1800, f1: 2600, f2: 1600, sweep: 0.8, q: 6 });
  },
);

registerSfx(
  'amb.knock',
  { label: 'ลำไผ่กระทบกัน', cat: 'ambient', vary: 0.12, gap: 0.3, preview: 1 },
  (k) => {
    // Hollow stems clacking in the wind, two or three times.
    const n = 2 + Math.floor(k.r() * 2);
    for (let i = 0; i < n; i++)
      woodblock(k, { f: k.range(700, 1000), at: i * k.range(0.09, 0.16), vol: 0.07 });
  },
);

registerSfx('amb.bubble', { label: 'น้ำพุร้อนเดือดปุด', cat: 'ambient', vary: 0.2, gap: 0.15 }, (k) => {
  for (let i = 0; i < 3; i++)
    k.tone({
      f: k.range(250, 450),
      f1: k.range(700, 1000),
      at: i * k.range(0.04, 0.1),
      dur: 0.05,
      vol: 0.04,
    });
});

registerSfx('amb.chime', { label: 'กระดิ่งลม', cat: 'ambient', vary: 0.03, gap: 0.5, preview: 2.4 }, (k) => {
  const n = 2 + Math.floor(k.r() * 3);
  for (let i = 0; i < n; i++)
    chime(k, {
      f: note(ROOT_D * 4, k.pick([0, 1, 2, 3, 4, 5])),
      at: i * k.range(0.12, 0.3),
      vol: 0.025,
      dur: 1.4,
    });
});

registerSfx('amb.wave', { label: 'คลื่นซัดฝั่ง', cat: 'ambient', vary: 0.08, gap: 1, preview: 3.5 }, (k) => {
  k.noise({ color: 'pink', dur: 2.2, vol: 0.2, attack: 1, f: 400, f1: 1100, f2: 500, sweep: 1.1, q: 0.6 });
  k.noise({ at: 1, dur: 1.6, vol: 0.06, attack: 0.2, type: 'highpass', f: 3500 });
});

// ───────── places and things ─────────

registerSfx('world.gong', { label: 'ฆ้องวัด', cat: 'world', vary: 0, gap: 1, preview: 4 }, (k) =>
  gong(k, { f: 110, dur: 3.6, vol: 0.15 }),
);

registerSfx(
  'world.lantern',
  { label: 'โคมไฟไหว (เปลวไฟ ไม้เอี๊ยด)', cat: 'world', vary: 0.1, gap: 0.5, preview: 1.2 },
  (k) => {
    // The flame fluttering in its paper, and the pole creaking.
    k.noise({ color: 'brown', dur: 0.8, vol: 0.12, attack: 0.1, type: 'lowpass', f: 400, trem: [9, 0.9] });
    const bp = k.filter('bandpass', 1100, 6);
    k.tone({
      type: 'sawtooth',
      f: 160,
      f1: 140,
      dur: 0.35,
      vol: 0.05,
      attack: 0.05,
      vib: [23, 0.05],
      out: bp,
    });
  },
);

registerSfx('world.warp', { label: 'วงวาร์ปทำงาน', cat: 'world', vary: 0, gap: 1, preview: 2 }, (k) => {
  k.tone({ f: 110, f1: 220, dur: 1.4, vol: 0.12, attack: 0.3 });
  k.noise({ dur: 1.2, vol: 0.2, attack: 0.9, f: 300, f1: 4000, q: 2, linear: true });
  for (let i = 0; i < 6; i++)
    k.tone({ f: note(587.33, i), at: 0.1 + i * 0.12, dur: 0.8, vol: 0.025, attack: 0.05 });
});

registerSfx(
  'world.warp.hum',
  { label: 'วงวาร์ปฮัม (วน)', cat: 'ambient', loop: true, vary: 0, preview: 3 },
  (k) => {
    k.tone({ f: 146.8, dur: Infinity, vol: 0.04, attack: 0.8, vib: [0.5, 0.01] });
    k.tone({ f: 220.4, dur: Infinity, vol: 0.025, attack: 1, vib: [0.7, 0.01] });
    k.noise({ dur: Infinity, vol: 0.03, attack: 1, f: 2400, q: 5, trem: [1.2, 0.8] });
  },
);

registerSfx('world.door', { label: 'ประตูไม้', cat: 'world', vary: 0.06, gap: 0.4, preview: 1 }, (k) => {
  // A wooden door creaking open, then its weight settling.
  const bp = k.filter('bandpass', 900, 8);
  k.tone({ type: 'sawtooth', f: 120, f1: 180, dur: 0.45, vol: 0.12, attack: 0.05, vib: [31, 0.08], out: bp });
  k.thump({ f0: 140, f1: 70, at: 0.42, dur: 0.15, vol: 0.25 });
  k.click({ f: 1200, at: 0.42, vol: 0.12 });
});

registerSfx('world.stairs', { label: 'ขึ้นลงบันได', cat: 'world', vary: 0.06, gap: 0.4 }, (k) => {
  for (let i = 0; i < 3; i++) {
    k.noise({ at: i * 0.16, dur: 0.07, vol: 0.25, attack: 0.001, f: k.range(420, 560), q: 7 });
    k.thump({ f0: 200, f1: 110, at: i * 0.16, dur: 0.07, vol: 0.12 });
  }
});

registerSfx('world.jail', { label: 'ประตูคุกปิด', cat: 'world', vary: 0, gap: 1, preview: 1.6 }, (k) => {
  k.thump({ f0: 90, f1: 35, dur: 0.4, vol: 0.5 });
  k.metal({ f: 340, dur: 0.9, vol: 0.08 });
  k.noise({ color: 'crackle', at: 0.15, dur: 0.6, vol: 0.5, f: 2800, q: 1.2 });
});

registerSfx(
  'world.guqin',
  { label: 'ดีดกู่ฉิน (ป้ายจุดฝึก)', cat: 'world', vary: 0, gap: 1, preview: 3 },
  (k) => {
    // A low open string, then a harmonic and a slide up a fifth: the banner of a training spot.
    k.pluck({ f: ROOT_D / 2, dur: 2.6, vol: 0.2, bright: 0.35 });
    k.pluck({ f: ROOT_D * 2, at: 0.35, dur: 1.6, vol: 0.06, bright: 0.9 });
    k.pluck({ f: (ROOT_D / 2) * 1.5, at: 0.7, dur: 2, vol: 0.14, bright: 0.35, bend: -0.06 });
  },
);

registerSfx('world.boss.spawn', { label: 'บอสปรากฏ', cat: 'world', vary: 0, gap: 2, preview: 3.5 }, (k) => {
  for (let i = 0; i < 6; i++)
    drum(k, { f: 58, at: i * 0.13 * (1 - i * 0.08), vol: 0.3 + i * 0.04, dur: 0.5 });
  gong(k, { f: 73.4, at: 0.75, dur: 2.6, vol: 0.16 });
  k.tone({
    type: 'sawtooth',
    f: 73.4,
    at: 0.75,
    dur: 2,
    vol: 0.08,
    attack: 0.3,
    out: k.filter('lowpass', 500),
  });
});

registerSfx(
  'world.boss.defeated',
  { label: 'ปราบบอสสำเร็จ', cat: 'world', vary: 0, gap: 2, preview: 4 },
  (k) => {
    gong(k, { f: 110, dur: 3.4, vol: 0.14 });
    strum(k, ROOT_D, [0, 2, 4, 5, 7, 9], { at: 0.3, gap: 0.08, vol: 0.12, dur: 2 });
    for (const at of [0, 0.25, 0.5]) drum(k, { f: 80, at, vol: 0.25 });
  },
);

registerSfx(
  'world.carriage',
  { label: 'รถม้าออกเดินทาง', cat: 'world', vary: 0.03, gap: 1, preview: 2.4 },
  (k) => {
    // Hooves on the road and the wheels creaking.
    for (let i = 0; i < 8; i++)
      woodblock(k, { f: i % 2 ? 620 : 520, at: i * 0.22 + (i % 2) * 0.05, vol: 0.09 });
    const bp = k.filter('bandpass', 700, 9);
    k.tone({ type: 'sawtooth', f: 90, f1: 110, dur: 1.8, vol: 0.05, attack: 0.3, vib: [3, 0.1], out: bp });
  },
);

registerSfx(
  'world.carriage.arrive',
  { label: 'รถม้าถึงที่หมาย', cat: 'world', vary: 0, gap: 1, preview: 1.8 },
  (k) => {
    chime(k, { f: 1046.5, vol: 0.06, dur: 1.2 });
    chime(k, { f: 1318.5, at: 0.15, vol: 0.05, dur: 1.2 });
    k.noise({ color: 'pink', at: 0.3, dur: 0.4, vol: 0.12, attack: 0.05, f: 700, f1: 300, q: 1 });
  },
);

// ───────── cutscenes (data/scenes.ts `sfx` cues) ─────────

registerSfx('scene.heartbeat', { label: 'เสียงหัวใจเต้น', cat: 'world', vary: 0, gap: 0.3 }, (k) => {
  k.thump({ f0: 72, f1: 44, dur: 0.18, vol: 0.5 });
  k.thump({ f0: 64, f1: 40, at: 0.26, dur: 0.2, vol: 0.38 });
});

registerSfx('scene.waves', { label: 'คลื่นซัด (ฉาก)', cat: 'world', vary: 0, gap: 1, preview: 4 }, (k) => {
  k.noise({ color: 'pink', dur: 2.6, vol: 0.26, attack: 0.9, f: 380, f1: 900, q: 0.6 });
  k.noise({ color: 'pink', at: 1.4, dur: 2.2, vol: 0.18, attack: 0.5, f: 900, f1: 300, q: 0.6 });
});

registerSfx(
  'scene.whisper',
  { label: 'เสียงกระซิบจากศิลา', cat: 'world', vary: 0, gap: 1, preview: 1.8 },
  (k) => {
    k.noise({ dur: 1.4, vol: 0.14, attack: 0.4, f: 2600, f1: 1700, q: 3, trem: [5, 0.6] });
    k.noise({ at: 0.3, dur: 1, vol: 0.06, attack: 0.3, f: 1200, f1: 900, q: 6 });
  },
);

registerSfx('scene.palmGlow', { label: 'ฝ่ามือเรืองแสง', cat: 'world', vary: 0, gap: 1, preview: 2 }, (k) => {
  k.tone({ f: 220, f1: 330, dur: 1.6, vol: 0.06, attack: 0.6 });
  k.noise({ dur: 1.6, vol: 0.08, attack: 1, f: 800, f1: 3000, q: 3 });
  chime(k, { f: 1174.7, at: 1, vol: 0.04 });
});

registerSfx('scene.shrineHum', { label: 'ศาลฮัม', cat: 'world', vary: 0, gap: 1, preview: 4 }, (k) => {
  bowl(k, { f: 146.8, dur: 3.6, vol: 0.09 });
  whoosh(k, { dur: 2.5, f0: 300, f1: 900, f2: 400, vol: 0.06, q: 2 });
});
